import {
  Apple,
  Bell,
  Check,
  ClipboardList,
  CreditCard,
  Dumbbell,
  House,
  Mail,
  MessageSquare,
  Play,
  Plus,
  Search,
  SquareCheck,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import AssistantFace from "@/components/AssistantFace";
import { BRAND, DISH, EX, Initials, Logo, PhotoAvatar, Pill } from "./primitives";

const MAIN = [
  { icon: House, label: "Dashboard" },
  { icon: Users, label: "Clients", screen: "Clients" },
  { icon: SquareCheck, label: "Check-ins" },
  { icon: MessageSquare, label: "Messages" },
];
const LIBRARY = [
  { icon: Dumbbell, label: "Workouts", screen: "Workouts" },
  { icon: Apple, label: "Nutrition", screen: "Nutrition" },
  { icon: ClipboardList, label: "Proposals" },
  { icon: CreditCard, label: "Payments" },
];

const CLIENTS = [
  { ini: "SC", tone: "amber", name: "Sarah Chen", plan: "Fat loss · 12 wk", status: ["New", "neutral"], latest: "Proposal sent", last: "Today" },
  { ini: "TW", tone: "blue", name: "Tom Walker", plan: "Strength · 16 wk", status: ["Active", "green"], latest: "Week 5 of 16", last: "2 days" },
  { ini: "AR", tone: "pink", name: "Aisha Rahman", plan: "Hypertrophy · 10 wk", status: ["Quiet 9 days", "amber"], latest: "Nudge drafted", last: "9 days" },
  { ini: "JM", tone: "violet", name: "James Moore", plan: "Recomp · 8 wk", status: ["Active", "green"], latest: "Week 2 of 8", last: "Today" },
  { ini: "LP", tone: "cyan", name: "Lena Park", plan: "Marathon prep", status: ["Paused", "neutral"], latest: "Back 3 Nov", last: "3 wk" },
  { ini: "DO", tone: "orange", name: "Daniel Obi", plan: "Fat loss · 12 wk", status: ["Active", "green"], latest: "Week 9 of 12", last: "Yesterday" },
];

const SCREEN = {
  "AI Agent": { title: "Clients", icon: Users, nav: "Clients" },
  Clients: { title: "Clients", icon: Users, nav: "Clients" },
  Workouts: { title: "Workouts", icon: Dumbbell, nav: "Workouts" },
  Nutrition: { title: "Nutrition", icon: Apple, nav: "Nutrition" },
};

function SideRow({ icon: Icon, label, active }) {
  return (
    <div
      className={cn(
        "flex h-8 items-center gap-2.5 rounded-lg px-2.5 text-[13.5px]",
        active ? "bg-neutral-100 font-medium text-ink" : "text-neutral-600",
      )}
    >
      <Icon className="h-[15px] w-[15px]" strokeWidth={1.8} />
      {label}
    </div>
  );
}

const GRID = "grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.4fr)] md:grid-cols-[36px_minmax(0,2.2fr)_minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,1fr)]";

function ClientsTable() {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3.5 sm:px-5">
        <Pill tone="dark">All clients</Pill>
        <Pill>Active · 31</Pill>
        <Pill className="hidden sm:inline-flex">Needs attention · 4</Pill>
        <Pill className="hidden sm:inline-flex">Paused · 3</Pill>
        <span className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-[10px] bg-black px-3 text-[13px] font-medium text-white">
          <Plus className="h-3.5 w-3.5" strokeWidth={2.2} />
          Add client
        </span>
      </div>
      <div className="mx-4 overflow-hidden rounded-[10px] border border-line sm:mx-5">
        <div className={cn(GRID, "h-10 items-center bg-soft px-4 text-[13px] font-medium text-neutral-600")}>
          <span className="hidden h-[15px] w-[15px] rounded border border-neutral-300 bg-white md:block" />
          <span>Name</span>
          <span>Status</span>
          <span className="hidden md:block">Latest</span>
          <span className="hidden md:block">Last check-in</span>
        </div>
        {CLIENTS.map((c, i) => (
          <div
            key={c.name}
            className={cn(GRID, "h-[52px] items-center border-t border-line px-4 text-[13.5px]", i === 0 && "bg-[#FFFDE6]")}
          >
            <span className="hidden h-[15px] w-[15px] rounded border border-neutral-300 md:block" />
            <div className="flex min-w-0 items-center gap-2.5">
              <Initials text={c.ini} tone={c.tone} />
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{c.name}</span>
                <span className="truncate text-xs text-neutral-500">{c.plan}</span>
              </div>
            </div>
            <div>
              <Pill tone={c.status[1]}>{c.status[0]}</Pill>
            </div>
            <span className="hidden text-neutral-600 md:block">{c.latest}</span>
            <span className="hidden text-neutral-500 md:block">{c.last}</span>
          </div>
        ))}
      </div>
    </>
  );
}

const DAYS = [
  { day: "Day 1", name: "Lower body", items: [[EX.gobletM, "4 × 8"], [EX.rdlF, "3 × 8"], [EX.lungeM, "3 × 12"]] },
  { day: "Day 2", name: "Upper body push", items: [[EX.benchM, "4 × 6 to 8"], [EX.ohpM, "3 × 10"], [EX.dbBenchF, "3 × 12"]] },
  { day: "Day 3", name: "Glutes", items: [[EX.hipF, "4 × 10"], [EX.gobletF, "3 × 12"]] },
];

function WorkoutsScreen() {
  return (
    <div className="grid gap-4 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_280px]">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Fat Loss Phase 1</span>
          <Pill>12 weeks</Pill>
          <Pill tone="lemon">Assigned to Sarah Chen</Pill>
        </div>
        {DAYS.map((d) => (
          <div key={d.day} className="rounded-[10px] border border-line">
            <div className="flex items-center gap-2 border-b border-line bg-soft px-3.5 py-2.5 text-[13px]">
              <span className="font-semibold">{d.day}</span>
              <span className="text-neutral-500">{d.name}</span>
            </div>
            {d.items.map(([ex, sets]) => (
              <div key={ex.src} className="flex items-center gap-2.5 border-t border-line px-3.5 py-1.5 text-[13px] first:border-0">
                <span className="h-10 w-12 shrink-0 overflow-hidden rounded-md border border-line bg-white"><img src={ex.src} alt="" loading="lazy" className="h-full w-full scale-[1.7] object-cover" /></span>
                <span className="font-medium">{ex.name}</span>
                <span className="text-neutral-500">· {sets}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="relative hidden flex-col overflow-hidden rounded-xl border border-line bg-white lg:flex">
        <div className="relative">
          <img src={EX.benchM.src} alt="Barbell bench press demo" className="aspect-video w-full object-cover" />
          <span className="absolute bottom-2.5 left-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-lemon shadow">
            <Play className="ml-0.5 h-3.5 w-3.5 fill-black text-black" />
          </span>
        </div>
        <div className="flex flex-col gap-2 p-3.5">
          <span className="text-sm font-semibold">Barbell bench press</span>
          <span className="text-xs text-neutral-500">Chest · barbell</span>
          <div className="flex w-max overflow-hidden rounded-full border border-line text-xs">
            <span className="bg-ink px-2.5 py-1 text-white">Men</span>
            <span className="px-2.5 py-1">Women</span>
          </div>
          <img src={EX.dbBenchF.src} alt="Dumbbell bench press, women's demo" className="mt-1 aspect-video w-full rounded-lg border border-line object-cover" />
        </div>
      </div>
    </div>
  );
}

function Macro({ label, value, pct, color }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between text-[12.5px]">
        <span className="text-neutral-600">{label}</span>
        <b className="font-semibold">{value}</b>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-neutral-100">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

const MEALS = [
  ["Breakfast", DISH.omelette],
  ["Lunch", DISH.bowl],
  ["Snack", DISH.yogurt],
  ["Dinner", DISH.salmon],
];

function NutritionScreen() {
  return (
    <div className="grid gap-4 p-4 sm:p-5 lg:grid-cols-[300px_minmax(0,1fr)]">
      <div className="flex flex-col gap-3.5 rounded-xl border border-line p-[18px]">
        <div className="flex justify-between text-[13px] text-neutral-500">
          <span>Daily target</span>
          <span>Sarah Chen</span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[34px] font-semibold tracking-[-0.8px]">1,900</span>
          <span className="text-[13px] text-neutral-500">kcal</span>
        </div>
        <Macro label="Protein" value="140 g" pct={80} color="#171717" />
        <Macro label="Carbs" value="190 g" pct={70} color="#A3A3A3" />
        <Macro label="Fat" value="60 g" pct={55} color="#D4D4D4" />
      </div>
      <div className="overflow-hidden rounded-xl border border-line">
        {MEALS.map(([m, d], i) => (
          <div key={m} className={cn("flex items-center gap-3 px-3 py-2.5 text-[13.5px] sm:px-4", i > 0 && "border-t border-line")}>
            <img src={d.src} alt={d.name} loading="lazy" className="h-14 w-14 shrink-0 rounded-lg object-cover sm:h-16 sm:w-16" />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="text-xs text-neutral-500">{m}</span>
              <span className="truncate font-medium">{d.name}</span>
            </div>
            <span className="shrink-0 text-neutral-500">{d.kcal} kcal</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AgentCard({ floating }) {
  const steps = [
    ["Created client", "Sarah Chen"],
    ["Assigned program", "Fat Loss Phase 1 · 12 weeks"],
    ["Meal plan", "1,900 kcal · 4 dish photos"],
  ];
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-2xl bg-[#0A0A0A] p-4 shadow-[0_24px_48px_-16px_rgba(0,0,0,0.45)]",
        floating ? "absolute bottom-6 right-6 hidden w-[340px] lg:flex" : "mx-4 mb-4 mt-4 sm:mx-5 lg:hidden",
      )}
    >
      <div className="flex items-center gap-2.5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-lemon/25">
          <AssistantFace className="h-9 w-9" />
        </span>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-neutral-400">You said</span>
          <span className="text-[13.5px] leading-snug text-white">
            "Add Sarah Chen, 12 weeks fat loss, and send her the proposal."
          </span>
        </div>
      </div>
      <div className="h-px bg-neutral-800" />
      {steps.map(([a, b]) => (
        <div key={a} className="flex items-center gap-2.5 text-[13px] text-neutral-200">
          <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-lemon">
            <Check className="h-[11px] w-[11px] text-black" strokeWidth={3} />
          </span>
          <span>
            {a} <b className="font-semibold text-white">{b}</b>
          </span>
        </div>
      ))}
      <div className="flex items-center gap-2.5 rounded-[10px] bg-[#1A1A1A] px-3 py-2.5">
        <Mail className="h-4 w-4 text-neutral-400" strokeWidth={1.8} />
        <span className="flex-1 text-[13px] text-neutral-200">Proposal email ready</span>
        <span className="flex h-7 items-center rounded-lg bg-lemon px-3 text-[12.5px] font-semibold text-black">
          Approve &amp; send
        </span>
      </div>
    </div>
  );
}

export default function AppPreview({ tab }) {
  const screen = SCREEN[tab];
  const TitleIcon = screen.icon;

  return (
    <div className="bg-gradient-to-b from-soft to-white px-3 pt-6 sm:px-8 sm:pt-8">
      <div
        aria-label="Fitamingo app preview"
        className="relative flex overflow-hidden rounded-t-[14px] border border-b-0 border-line bg-white shadow-[0_30px_60px_-30px_rgba(0,0,0,0.18)] lg:h-[600px]"
      >
        <aside className="hidden w-[232px] shrink-0 flex-col gap-1 border-r border-line px-3 py-3.5 md:flex">
          <div className="px-1.5 pb-3.5 pt-0.5">
            <Logo size={26} />
          </div>
          <div className="mb-2 flex h-8 items-center gap-2 rounded-lg border border-line px-2.5 text-[13px] text-neutral-400">
            <Search className="h-3.5 w-3.5" strokeWidth={2} /> Search
          </div>
          <span className="px-2.5 pb-0.5 pt-1.5 text-xs font-medium text-neutral-400">Main</span>
          {MAIN.map((m) => (
            <SideRow key={m.label} {...m} active={m.screen === screen.nav} />
          ))}
          <span className="px-2.5 pb-0.5 pt-3 text-xs font-medium text-neutral-400">Library</span>
          {LIBRARY.map((m) => (
            <SideRow key={m.label} {...m} active={m.screen === screen.nav} />
          ))}
          <div className="mt-auto flex items-center gap-2.5 border-t border-line p-2.5">
            <PhotoAvatar src={BRAND.martin} size={30} />
            <div className="flex flex-col">
              <span className="text-[13px] font-medium">Martin</span>
              <span className="text-xs text-neutral-500">Head coach</span>
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col pb-4 lg:pb-0">
          <div className="flex h-14 shrink-0 items-center gap-3 border-b border-line px-4 sm:px-5">
            <TitleIcon className="h-[18px] w-[18px]" strokeWidth={1.8} />
            <span className="text-base font-semibold">{screen.title}</span>
            <span className="h-[18px] w-px bg-line" />
            <span className="text-[13px] text-neutral-500">
              {screen.nav === "Clients" ? "38 active" : screen.nav === "Workouts" ? "24 programs" : "16 plans"}
            </span>
            <div className="ml-auto flex items-center gap-3.5">
              <Bell className="h-[17px] w-[17px] text-neutral-600" strokeWidth={1.8} />
              <div className="flex">
                <PhotoAvatar src={BRAND.martin} size={28} />
                <PhotoAvatar src={BRAND.nasim} size={28} className="-ml-2" />
              </div>
            </div>
          </div>
          {screen.nav === "Clients" && <ClientsTable />}
          {screen.nav === "Workouts" && <WorkoutsScreen />}
          {screen.nav === "Nutrition" && <NutritionScreen />}
          {tab === "AI Agent" && <AgentCard floating={false} />}
        </div>

        {tab === "AI Agent" && <AgentCard floating />}
      </div>
    </div>
  );
}
